/**
 * Wisdom Dream announcement-bar rotator.
 *
 * Shopify/Horizon owns the announcement markup and editor blocks. This
 * enhancer only owns slide state, so it avoids racing the native component's
 * animation internals while keeping the bar calm and accessible.
 */
(function () {
  'use strict';

  var INTERVAL_MS = 6500;
  var ANIMATION_MS = 640;
  var ACTIVE_CLASS = 'wd-announcement-slide--active';
  var ENTERING_CLASS = 'wd-announcement-slide--entering';
  var EXITING_CLASS = 'wd-announcement-slide--exiting';
  var READY_CLASS = 'wd-announcement-rotator-ready';
  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  function initRotator() {
    var bar = document.querySelector('announcement-bar-component');
    if (!bar) return;

    var slides = Array.prototype.slice.call(bar.querySelectorAll('slideshow-slide'));
    if (slides.length < 2) return;

    var mediaQuery = window.matchMedia ? window.matchMedia(REDUCED_MOTION_QUERY) : null;
    var currentIndex = 0;
    var timerId = null;
    var settleTimerId = null;
    var isAnimating = false;
    var hoverPaused = false;
    var focusStopped = false;

    // Let this small controller own state instead of running beside Horizon's
    // autoplay timer. Also avoid screen readers announcing timed changes.
    bar.removeAttribute('autoplay');
    bar.setAttribute('aria-live', 'off');
    if (typeof bar.pause === 'function') bar.pause();

    function prefersReducedMotion() {
      return Boolean(mediaQuery && mediaQuery.matches);
    }

    function clearMotionState(slide) {
      slide.classList.remove(ENTERING_CLASS, EXITING_CLASS);
    }

    function setActive(index) {
      if (settleTimerId !== null) {
        window.clearTimeout(settleTimerId);
        settleTimerId = null;
      }

      slides.forEach(function (slide, slideIndex) {
        var isActive = slideIndex === index;
        clearMotionState(slide);
        slide.classList.toggle(ACTIVE_CLASS, isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
      currentIndex = index;
      isAnimating = false;
    }

    function nextIndex() {
      return (currentIndex + 1) % slides.length;
    }

    function finishAnimation(index) {
      settleTimerId = null;
      setActive(index);
    }

    function animateTo(index) {
      if (isAnimating || index === currentIndex) return;

      var outgoingSlide = slides[currentIndex];
      var incomingSlide = slides[index];

      isAnimating = true;

      slides.forEach(function (slide) {
        clearMotionState(slide);
        if (slide !== outgoingSlide && slide !== incomingSlide) {
          slide.classList.remove(ACTIVE_CLASS);
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      outgoingSlide.classList.add(EXITING_CLASS);
      outgoingSlide.setAttribute('aria-hidden', 'true');

      incomingSlide.classList.remove(ACTIVE_CLASS);
      incomingSlide.classList.add(ENTERING_CLASS);
      incomingSlide.setAttribute('aria-hidden', 'false');

      settleTimerId = window.setTimeout(function () {
        finishAnimation(index);
      }, ANIMATION_MS);
    }

    function advance() {
      if (hoverPaused || focusStopped || prefersReducedMotion() || isAnimating) return;
      animateTo(nextIndex());
    }

    function start() {
      if (timerId !== null || focusStopped || prefersReducedMotion() || document.hidden) return;
      timerId = window.setInterval(advance, INTERVAL_MS);
    }

    function stop() {
      if (timerId === null) return;
      window.clearInterval(timerId);
      timerId = null;
    }

    function stopForFocus() {
      focusStopped = true;
      stop();
    }

    function handleMotionPreferenceChange() {
      if (prefersReducedMotion()) {
        stop();
        setActive(0);
      } else {
        start();
      }
    }

    setActive(0);
    bar.classList.add(READY_CLASS);

    bar.addEventListener('mouseenter', function () {
      hoverPaused = true;
      stop();
    });

    bar.addEventListener('mouseleave', function () {
      hoverPaused = false;
      start();
    });

    bar.addEventListener('focusin', stopForFocus);

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else start();
    });

    if (mediaQuery) {
      if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', handleMotionPreferenceChange);
      } else if (typeof mediaQuery.addListener === 'function') {
        mediaQuery.addListener(handleMotionPreferenceChange);
      }
    }

    if (prefersReducedMotion()) {
      setActive(0);
      return;
    }

    start();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRotator);
  } else {
    initRotator();
  }
})();
